import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { MapPin, Phone, Star, Search, UtensilsCrossed, Coffee, Scissors, Shirt, Cookie, Package, Footprints, Hammer, Sparkles, Sandwich, Gem, BookOpen, PawPrint, Glasses, IceCreamCone, Pizza } from 'lucide-react';
import { BUSINESS_CATEGORIES } from '@/lib/categories';
import { StarDisplay } from '@/components/StarRating';
import Navbar from '@/components/Navbar';
import { useLang } from '@/contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  abbigliamento: Shirt, bar: Coffee, calzature: Footprints, casa_ferramenta: Hammer,
  centro_estetico: Sparkles, gastronomia: Sandwich, gioielleria: Gem, libreria: BookOpen,
  animali: PawPrint, ottica: Glasses, panetteria: Cookie, parrucchiere: Scissors,
  pasticceria: IceCreamCone, pizzeria: Pizza, ristorante: UtensilsCrossed, altro: Package,
};
const CATEGORY_META = Object.fromEntries(
  BUSINESS_CATEGORIES.map(c => [c, { icon: CATEGORY_ICONS[c], key: `cat_${c}` }])
);
const CATEGORY_ORDER = BUSINESS_CATEGORIES;
const RESTAURANT_IMAGES = [
  "https://images.unsplash.com/photo-1685040235380-a42a129ade4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjByZXN0YXVyYW50JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc2MzI2Mzk5fDA&ixlib=rb-4.1.0&q=85",
  "https://images.pexels.com/photos/33357444/pexels-photo-33357444.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.unsplash.com/photo-1613274554329-70f997f5789f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjByZXN0YXVyYW50JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc2MzI2Mzk5fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1762087577613-978bf9066d39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjByZXN0YXVyYW50JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc2MzI2Mzk5fDA&ixlib=rb-4.1.0&q=85",
];

export default function RestaurantList() {
  const { t } = useLang();
  const [restaurants, setRestaurants] = useState([]);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/restaurants`); setRestaurants(res.data); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = restaurants.filter(r => {
    if (activeCategory !== 'all' && (r.category || 'altro') !== activeCategory) return false;
    const q = search.toLowerCase();
    if (!q) return true;
    return r.name.toLowerCase().includes(q) ||
      (r.cuisine || '').toLowerCase().includes(q) ||
      (r.address || '').toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8" data-testid="restaurant-list-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pt-20 sm:pt-24">
        <div className="mb-6 sm:mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-2 font-sans">{t('rest_discover')}</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t('rest_title')}</h1>
        </div>
        <div className="relative mb-4 sm:mb-5">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input data-testid="restaurant-search" placeholder={t('rest_search')} value={search} onChange={e => setSearch(e.target.value)} className="pl-11 rounded-xl border-border h-11 sm:h-12 text-sm font-sans" />
        </div>
        {/* Category filter chips */}
        <div className="flex gap-2 overflow-x-auto pb-3 mb-4 sm:mb-6 -mx-1 px-1 scrollbar-none" data-testid="category-filter">
          <button
            onClick={() => setActiveCategory('all')}
            data-testid="cat-chip-all"
            className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-sans font-medium transition-colors border ${activeCategory === 'all' ? 'bg-primary text-white border-primary' : 'bg-card text-muted-foreground border-border hover:text-foreground'}`}
          >
            {t('cat_all')}
          </button>
          {CATEGORY_ORDER.map(cat => {
            const Icon = CATEGORY_META[cat].icon;
            const active = activeCategory === cat;
            return (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                data-testid={`cat-chip-${cat}`}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-sans font-medium transition-colors border ${active ? 'bg-primary text-white border-primary' : 'bg-card text-muted-foreground border-border hover:text-foreground'}`}
              >
                <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                {t(CATEGORY_META[cat].key)}
              </button>
            );
          })}
        </div>
        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 sm:py-20"><p className="text-muted-foreground font-sans">{t('rest_no_results')}</p></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filtered.map((r, i) => (
              <Card key={r.restaurant_id} className="border border-border rounded-2xl overflow-hidden card-hover cursor-pointer" onClick={() => navigate(`/restaurants/${r.restaurant_id}`)} data-testid={`restaurant-card-${r.restaurant_id}`}>
                <div className="h-36 sm:h-48 relative overflow-hidden">
                  <img src={r.image_url || RESTAURANT_IMAGES[i % RESTAURANT_IMAGES.length]} alt={r.name} className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" loading="lazy" />
                  <Badge className="absolute top-3 right-3 bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] sm:text-xs">{t('rest_earn')}</Badge>
                </div>
                <CardContent className="p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-bold mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{r.name}</h3>
                  {r.category && CATEGORY_META[r.category] && (
                    <div className="inline-flex items-center gap-1 mb-1.5">
                      {React.createElement(CATEGORY_META[r.category].icon, { className: 'w-3 h-3 text-primary', strokeWidth: 1.5 })}
                      <p className="text-[10px] sm:text-xs text-primary font-semibold uppercase tracking-wide font-sans">{t(CATEGORY_META[r.category].key)}</p>
                    </div>
                  )}
                  {r.avg_rating > 0 && <div className="mb-1.5"><StarDisplay value={r.avg_rating} size="xs" /></div>}
                  <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground font-sans"><MapPin className="w-3 sm:w-3.5 h-3 sm:h-3.5" strokeWidth={1.5} /><span className="truncate" data-testid={`card-city-${r.restaurant_id}`}>{r.city || r.address || '-'}</span></div>
                  {r.contacts && <div className="flex items-start gap-1 text-xs sm:text-sm text-muted-foreground mt-1 font-sans" data-testid={`card-contacts-${r.restaurant_id}`}><Phone className="w-3 sm:w-3.5 h-3 sm:h-3.5 mt-0.5 shrink-0" strokeWidth={1.5} /><span className="line-clamp-2">{r.contacts}</span></div>}
                  <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground mt-1 font-sans"><Star className="w-3 sm:w-3.5 h-3 sm:h-3.5" strokeWidth={1.5} /><span>{r.review_count || 0} {t('rest_reviews')}</span></div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
